import React from "react";
import classnames from "classnames";
import PropTypes from "prop-types";

import IconView from "@/shared/components/IconView/IconView";

import EgwWebFont from "src/assets/EgwWebFont";
import { CONTENT_CLASSES } from "@/shared/utils/content/content.constants";
import { paraPictureRegExp } from "@/shared/utils/contact-us/constants";
import {
  DATA_ATTR_PARA_ID,
  READER_VIEW_CLASS_NAME_PARA_WRAP,
  READER_VIEW_CLASS_NAME_PARA_CONTENT_ONLY,
} from "@/shared/utils/reader/constants";
import BookInfoInReader from "./BookInfoInReader";

import styles from "./para-list.module.scss";

const isSkipRenderPara = (para) => para.element_type === "h1";
const isParaPicture = (para) => para.content.indexOf("Picture:") > -1;

const ParaList = ({
  firstParaOffsetTop,
  showTranslations,
  showRefCodes,
  activeId,
  items,
  onClickTranslate,
  onClickAnchor,
}) => {
  if (!items.paras) {
    return null;
  }

  if (items.paras[0].isBookInfo) {
    const { book, para_id } = items.paras[0];
    return <BookInfoInReader book={book} offsetTop={firstParaOffsetTop} />;
  }

  const hasChapterImage = items.paras.some((para) => isParaPicture(para));

  return items.paras.map((item, index, arr) => {
    let style;

    if (
      !hasChapterImage &&
      (index === 0 || (index === 1 && isSkipRenderPara(arr[0]) && firstParaOffsetTop))
    ) {
      style = {
        paddingTop: firstParaOffsetTop,
      };
    }

    const Element = item.element_type;
    const id = item["para_id"];

    if (isSkipRenderPara(item)) {
      return null;
    }

    if (isParaPicture(item)) {
      return (
        <img
          key={index}
          className={styles[CONTENT_CLASSES.PICTURE]}
          style={{
            paddingTop: firstParaOffsetTop,
          }}
          src={item.content.match(paraPictureRegExp)[2]}
        />
      );
    }

    if (Element === "p") {
      let btnTranslation;

      if (showTranslations && item?.translations?.length) {
        btnTranslation = (
          <IconView
            icon={EgwWebFont.translation}
            onClick={(e) => {
              onClickTranslate(e, item);
            }}
            className={classnames("reader-translate-icon", CONTENT_CLASSES.ICON_TRANSLATE)}
            data-translations={id}
          />
        );
      }

      return (
        <Element
          className={classnames(
            styles["para"],
            READER_VIEW_CLASS_NAME_PARA_WRAP,
            CONTENT_CLASSES.PARAGRAPH,
            item.element_subtype,
            {
              active: activeId === id,
            },
          )}
          style={style}
          id={id}
          {...{
            [DATA_ATTR_PARA_ID]: id,
          }}
          key={index}>
          <span
            className={READER_VIEW_CLASS_NAME_PARA_CONTENT_ONLY}
            dangerouslySetInnerHTML={{ __html: item.content }}
          />
          {/*nbsp to not glue the refcode to the text or the next paragraph on print/copy*/}
          &nbsp;
          {item.refcode_short && showRefCodes && (
            <span
              className={classnames(styles["refCode"], CONTENT_CLASSES.REF_CODE)}
              onClick={(e) => onClickAnchor(e, item)}>
              {item.refcode_short}
              <span className="anchor-link">
                <i className="icon-anchor" />
              </span>
            </span>
          )}
          {btnTranslation}
        </Element>
      );
    }

    return (
      <Element
        key={index}
        id={id}
        {...{
          [DATA_ATTR_PARA_ID]: id,
        }}
        className={classnames(
          styles["para"],
          READER_VIEW_CLASS_NAME_PARA_WRAP,
          READER_VIEW_CLASS_NAME_PARA_CONTENT_ONLY,
          item.element_type,
          item.element_subtype,
          {
            active: activeId === id,
          },
        )}
        style={style}
        // an empty space to not glue the text to the next paragraph on print/copy
        dangerouslySetInnerHTML={{ __html: item.content + " " }}
      />
    );
  });
};

ParaList.propTypes = {
  showTranslations: PropTypes.bool,
  showRefCodes: PropTypes.bool,
  activeId: PropTypes.string,
  items: PropTypes.object,
  onClick: PropTypes.func,
  onClickAnchor: PropTypes.func,
  onClickTranslate: PropTypes.func,
};

export default ParaList;
